import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Form,
  Image,
  Input,
  InputNumber,
  Select,
  Upload,
} from 'antd';
import {
  IndustryApiCall,
  SectorApiCall,
  SubSectorApiCall,
  SuperSectorApiCall,
} from 'apps/catalog/apiCalls/stock-icb';
import { stockStatusOptions } from 'apps/investment/constants/fundProduct';
import { useFetchApiList } from 'hooks/api';
import { useEffect, useMemo } from 'react';
import { formItemLayout, tailFormItemLayout } from 'utils/formConfig';

const ICBPageSize = { page_size: 500 };

// Stock (base product) edit form. ETF-specific attributes — including the fund
// manager — live on the ETF profile and are edited separately via ETFProfileEditForm.
const ItemForm = ({
  form,
  submiting,
  onFinish,
  initialValues,
  name,
  onDelete,
  visibility = 'hidden',
}) => {
  const { logo } = initialValues || {};

  const normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  // Load all ICB levels once on mount
  const { data: industries, load: loadIndustries } = useFetchApiList(
    IndustryApiCall.list,
    { resourceName: 'data' }
  );
  const { data: allSuperSectors, load: loadSuperSectors } = useFetchApiList(
    SuperSectorApiCall.list,
    { resourceName: 'data' }
  );
  const { data: allSectors, load: loadSectors } = useFetchApiList(
    SectorApiCall.list,
    { resourceName: 'data' }
  );
  const { data: allSubSectors, load: loadSubSectors } = useFetchApiList(
    SubSectorApiCall.list,
    { resourceName: 'data' }
  );

  useEffect(() => {
    loadIndustries(ICBPageSize);
    loadSuperSectors(ICBPageSize);
    loadSectors(ICBPageSize);
    loadSubSectors(ICBPageSize);
  }, []);

  // Watch parent selections for cascading
  const selectedIndustryCode = Form.useWatch('icb_industry_code', form);
  const selectedSuperSectorCode = Form.useWatch('icb_supersector_code', form);
  const selectedSectorCode = Form.useWatch('icb_sector_code', form);

  const superSectorOptions = useMemo(
    () =>
      (allSuperSectors || []).filter(
        ss => !selectedIndustryCode || ss.industry_code === selectedIndustryCode
      ),
    [allSuperSectors, selectedIndustryCode]
  );

  const sectorOptions = useMemo(
    () =>
      (allSectors || []).filter(
        s =>
          !selectedSuperSectorCode ||
          s.supersector_code === selectedSuperSectorCode
      ),
    [allSectors, selectedSuperSectorCode]
  );

  const subSectorOptions = useMemo(
    () =>
      (allSubSectors || []).filter(
        ss => !selectedSectorCode || ss.sector_code === selectedSectorCode
      ),
    [allSubSectors, selectedSectorCode]
  );

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  return (
    <Form
      {...formItemLayout}
      form={form}
      name={name || 'product_form'}
      onFinish={values => onFinish(values, ['logo'])}
      initialValues={initialValues}
      scrollToFirstError
    >
      <Form.Item name="symbol" label="Symbol">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item
        name="logo"
        label="Logo"
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload.Dragger
          listType="picture"
          beforeUpload={() => false}
          accept="image/*"
          maxCount={1}
          showUploadList={true}
          onChange={info => {
            form.setFieldValue('logo', info.fileList);
          }}
        >
          <Button icon={<UploadOutlined />}>Click to Upload Logo</Button>
        </Upload.Dragger>
        {logo && <Image width={100} src={logo} />}
      </Form.Item>

      <Form.Item name="name" label="Name">
        <Input placeholder="" />
      </Form.Item>
      <Form.Item name="exchange" label="Exchange">
        <Select allowClear placeholder="Select exchange">
          <Select.Option value="HOSE">HOSE</Select.Option>
          <Select.Option value="HNX">HNX</Select.Option>
          <Select.Option value="UPCOM">UPCOM</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item name="description" label="Description">
        <Input.TextArea rows={10} />
      </Form.Item>

      <Form.Item name="status" label="Status">
        <Select>
          {stockStatusOptions &&
            stockStatusOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="ranking"
        label="Ranking"
        tooltip="Higher ranking is shown first in listings"
      >
        <InputNumber min={0} placeholder="0" style={{ width: '100%' }} />
      </Form.Item>

      <Divider orientation="left" orientationMargin={0}>
        ICB Classification
      </Divider>

      <Form.Item name="icb_industry_code" label="Industry (L1)">
        <Select
          showSearch
          allowClear
          placeholder="Select industry"
          optionFilterProp="label"
          options={(industries || []).map(i => ({
            value: i.code,
            label: `${i.code} · ${i.name}`,
          }))}
          onChange={() => {
            form.setFieldsValue({
              icb_supersector_code: null,
              icb_sector_code: null,
              icb_subsector_code: null,
            });
          }}
        />
      </Form.Item>

      <Form.Item name="icb_supersector_code" label="SuperSector (L2)">
        <Select
          showSearch
          allowClear
          placeholder="Select supersector"
          optionFilterProp="label"
          options={superSectorOptions.map(ss => ({
            value: ss.code,
            label: `${ss.code} · ${ss.name}`,
          }))}
          onChange={() => {
            form.setFieldsValue({
              icb_sector_code: null,
              icb_subsector_code: null,
            });
          }}
        />
      </Form.Item>

      <Form.Item name="icb_sector_code" label="Sector (L3)">
        <Select
          showSearch
          allowClear
          placeholder="Select sector"
          optionFilterProp="label"
          options={sectorOptions.map(s => ({
            value: s.code,
            label: `${s.code} · ${s.name}`,
          }))}
          onChange={() => {
            form.setFieldsValue({ icb_subsector_code: null });
          }}
        />
      </Form.Item>

      <Form.Item name="icb_subsector_code" label="SubSector (L4)">
        <Select
          showSearch
          allowClear
          placeholder="Select subsector"
          optionFilterProp="label"
          options={subSectorOptions.map(ss => ({
            value: ss.code,
            label: `${ss.code} · ${ss.name}`,
          }))}
        />
      </Form.Item>

      <Form.Item {...tailFormItemLayout}>
        <Button type="primary" htmlType="submit" loading={submiting}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ItemForm;
